import React from 'react'
import { getSession } from '../game/model/sessionHost'
import { formatCompactNumber } from '../game/model/formatNumber'
import { nextTargetingMode } from '../game/model/targeting'
import { TOWER_TRAY_ORDER } from '../game/model/towers'
import {
  emitTutorialTargeting,
  emitTutorialUpgraded
} from '../onboarding/useMatchTutorial'
import { useGameStore } from '../store/gameStore'

function colorCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`
}

/** Match-only tower controls under the viewport: catalog or selected-tower actions. */
export const TowerBar: React.FC = () => {
  const {
    gameState,
    sessionView,
    placementDefinitionId,
    selectedTowerId,
    setPlacementDefinition,
    cancelPlacement,
    selectTowerId,
    upgradeTower,
    sellTower,
    isPaused
  } = useGameStore()
  const inMatch = gameState === 'playing' && sessionView?.state !== 'DEFEAT' && sessionView?.state !== 'VICTORY'
  if (!inMatch) return null

  const gold = sessionView?.gold ?? 0
  const session = getSession()
  const tray = session?.listAvailableTowers() ?? TOWER_TRAY_ORDER
  const snapshot = selectedTowerId
    ? session?.getTowerSnapshot(selectedTowerId) ?? null
    : null

  if (snapshot) {
    const upgradeDisabled = !snapshot.canUpgrade || !snapshot.canAffordUpgrade
    const upgradeLabel = snapshot.canUpgrade
      ? `UP ${snapshot.nextUpgrade!.cost}`
      : 'MAX'
    return (
      <div className="tower-bar tower-bar--inspect" aria-label="Selected tower">
        <div className="tower-inspect">
          <span
            className="tower-inspect__swatch"
            style={{
              ['--tower-color' as string]: colorCss(snapshot.color)
            }}
            aria-hidden
          >
            <span className={`tower-glyph tower-glyph--${snapshot.definitionId}`} />
          </span>
          <div className="tower-inspect__identity">
            <span className="tower-inspect__title">
              {snapshot.name.toUpperCase()} · LV {snapshot.level}
            </span>
            <span className="tower-inspect__stats">
              DMG {Math.round(snapshot.damage * 10) / 10} · RNG{' '}
              {Math.round(snapshot.range * 100) / 100} · SPD {snapshot.attackInterval.toFixed(2)}s
            </span>
            <span className="tower-inspect__combat" title="Match damage dealt / kills">
              DEALT {formatCompactNumber(snapshot.damageDealt)} · KILLS {snapshot.kills}
            </span>
          </div>
          <button
            type="button"
            className="tower-inspect__targeting"
            data-testid="cycle-targeting"
            data-tutorial-target="targeting"
            title="Cycle targeting mode"
            onClick={(event) => {
              event.preventDefault()
              const session = getSession()
              if (!session) return
              const next = nextTargetingMode(snapshot.targetingMode)
              session.setTowerTargeting(snapshot.id, next)
              useGameStore.getState().publishSessionView(session.view())
              emitTutorialTargeting()
            }}
          >
            TARGET: {snapshot.targetingMode}
          </button>
          <div className="tower-inspect__actions">
            <button
              type="button"
              className={`tower-inspect__btn tower-inspect__btn--upgrade ${
                upgradeDisabled ? 'tower-inspect__btn--locked' : ''
              }`}
              data-tutorial-target="upgrade"
              disabled={upgradeDisabled}
              onPointerUp={(event) => {
                event.preventDefault()
                if (upgradeTower(snapshot.id)) {
                  emitTutorialUpgraded()
                }
              }}
            >
              {upgradeLabel}
            </button>
            <button
              type="button"
              className="tower-inspect__btn tower-inspect__btn--sell"
              onPointerUp={(event) => {
                event.preventDefault()
                sellTower(snapshot.id)
              }}
            >
              SELL {snapshot.sellRefund}
            </button>
            <button
              type="button"
              className="tower-inspect__btn tower-inspect__btn--close"
              aria-label="Deselect tower"
              onPointerUp={(event) => {
                event.preventDefault()
                selectTowerId(null)
              }}
            >
              X
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tower-bar" aria-label="Tower bar">
      <div className="tower-tray">
        {tray.map((tower) => {
          const cost = tower.levels[0]?.cost ?? 0
          const selected = placementDefinitionId === tower.id
          const unaffordable = gold < cost || isPaused
          return (
            <button
              key={tower.id}
              type="button"
              disabled={isPaused}
              data-tower={tower.id}
              data-tutorial-target={tower.id === 'fire' ? 'fire' : undefined}
              aria-label={`${tower.name} ${cost}${selected ? ', selected' : ''}${unaffordable ? ', unaffordable' : ''}`}
              aria-pressed={selected}
              className={`tower-tray__item ${selected ? 'tower-tray__item--selected' : ''} ${
                unaffordable ? 'tower-tray__item--locked' : ''
              }`}
              style={{
                ['--tower-color' as string]: colorCss(tower.color)
              }}
              onPointerUp={(event) => {
                event.preventDefault()
                if (isPaused) return
                if (selected) cancelPlacement()
                else setPlacementDefinition(tower.id)
              }}
            >
              <span className="tower-tray__swatch" aria-hidden>
                <span className={`tower-glyph tower-glyph--${tower.id}`} />
              </span>
              <span className="tower-tray__meta">
                <span className="tower-tray__name">{tower.name}</span>
                <span className="tower-tray__cost">{cost}</span>
              </span>
              {selected ? (
                <span className="tower-tray__selected-mark" aria-hidden>
                  ●
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      {placementDefinitionId && (
        <button
          type="button"
          className="tower-bar__cancel"
          onPointerUp={(event) => {
            event.preventDefault()
            cancelPlacement()
          }}
        >
          <span className="tower-bar__cancel-label">Cancel</span>
          <span className="tower-bar__cancel-icon" aria-hidden>
            ✕
          </span>
        </button>
      )}
    </div>
  )
}
